// src/pages/purchaseOrder/EditPurchaseOrderPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Package, 
  X, 
  AlertCircle,
  FileText,
  DollarSign,
  ShoppingCart,
  Lock,
  CreditCard
} from 'lucide-react';
import { 
  getPurchaseOrderById, 
  updatePurchaseOrder 
} from '../../services/api/purchaseOrder';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { useAuth } from '../../hooks/useAuth';
import usePurchaseOrderForm from '../../hooks/usePurchaseOrderForm';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import BasicInformationSection from './components/BasicInformationSection';
import PurchaseOrderItemsSection from './components/PurchaseOrderItemsSection';
import FinancialDetailsSection from './components/FinancialDetailsSection';
import PaymentRecordsSection from '../orders/components/PaymentRecordsSection';
import { getProcurementItemImage } from '../../utils/imageUtils';
import styles from './CreatePurchaseOrderPage.module.css';

const EditPurchaseOrderPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { user } = useAuth();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('pending');
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const [isEditable, setIsEditable] = useState(true);
  const [lockedByReceipts, setLockedByReceipts] = useState(false);
  
  const {
    formData,
    warehouses,
    suppliers,
    paymentMethods,
    isLoading: isLoadingForm,
    errors,
    totals,
    handleFieldChange,
    addItem,
    updateItem,
    removeItem,
    addSupplier,
    addPaymentRecord,
    updatePaymentRecord,
    removePaymentRecord,
    validateForm,
    getPaymentRecordsPayload,
    setFormData,
    setErrors
  } = usePurchaseOrderForm(orderData);

  useEffect(() => {
    const loadPurchaseOrder = async () => {
      try {
        setIsLoadingOrder(true);
        const response = await getPurchaseOrderById(orderId);
        const order = response.purchaseOrder;
        
        const nonEditableStatuses = ['received', 'cancelled'];
        setLockedByReceipts(!!order.hasActiveReceipts);
        setIsEditable(!nonEditableStatuses.includes(order.status) && !order.hasActiveReceipts);
        
        const transformedData = {
          warehouseId: order.warehouseId,
          supplierId: order.supplierId,
          orderDate: order.orderDate.split('T')[0],
          expectedDeliveryDate: order.expectedDeliveryDate ? order.expectedDeliveryDate.split('T')[0] : '',
          items: order.items.map(item => {
            let displayName = item.Product.name;
            let sku = item.Product.sku;
            
            if (item.variationId && item.ProductVariation) {
              sku = item.ProductVariation.sku || item.Product.sku;
              
              if (item.ProductVariation.attributeValues && item.ProductVariation.attributeValues.length > 0) {
                const attributesString = item.ProductVariation.attributeValues
                  .map(attr => `${attr.ProductAttribute.name}: ${attr.value}`)
                  .join(', ');
                displayName = `${item.Product.name} (${attributesString})`;
              }
            }
            
            return {
              id: item.id,
              productId: item.productId,
              variationId: item.variationId,
              displayName: displayName,
              sku: sku,
              imageUrl: getProcurementItemImage(item),
              quantity: parseInt(item.quantity, 10),
              unitPrice: parseFloat(item.unitPrice),
              lineTotal: parseFloat(item.lineTotal),
              notes: item.notes || ''
            };
          }),
          extraCharges: order.extraCharges || '',
          extraChargesDescription: order.extraChargesDescription || '',
          discountAmount: order.discountAmount || '',
          discountDescription: order.discountDescription || '',
          notes: order.notes || '',
          status: order.status,
          autoReceive: false,
          paymentRecords: (order.supplierPayments || []).map(payment => ({
            id: payment.id,
            paymentMethodKey: payment.paymentMethod?.key || '',
            paymentAmount: parseFloat(payment.paymentAmount),
            reference: payment.reference || '',
            proofUrl: payment.proofUrl || null,
            paymentDate: payment.paymentDate ? payment.paymentDate.split('T')[0] : '',
            isDebitNote: !!payment.debitNoteId,
            // Debit-note offsets are locked (read-only) via the section's existing verified-record UI
            isVerified: !!payment.debitNoteId
          }))
        };
        
        setOrderData(transformedData);
      } catch (err) {
        console.error('Failed to load purchase order:', err);
        error(err.response?.data?.message || t('purchaseOrder.failedToLoadOrder'), {
          title: t('common.error'),
          duration: 6000
        });
        navigate('/inventory/purchase-orders');
      } finally {
        setIsLoadingOrder(false);
      }
    };
    
    if (orderId) {
      loadPurchaseOrder();
    }
  }, [orderId, navigate, error, t]);

  const handleSubmit = async (status = 'pending') => {
    if (!isEditable) {
      error(t('purchaseOrder.cannotEditOrder'), {
        title: t('common.error'),
        duration: 4000
      });
      return;
    }

    if (!validateForm()) {
      error(t('purchaseOrder.pleaseFixErrors'), {
        title: t('common.validationError'),
        duration: 5000
      });
      return;
    }

    if ((status === 'pending' || status === 'submitted') && !showConfirmDialog) {
      setPendingStatus(status);
      setShowConfirmDialog(true);
      return;
    }

    setIsSubmitting(true);
    setShowConfirmDialog(false);

    try {
      const updateData = {
        warehouseId: formData.warehouseId,
        supplierId: formData.supplierId,
        orderDate: formData.orderDate,
        expectedDeliveryDate: formData.expectedDeliveryDate || null,
        items: formData.items.map(item => ({
          productId: item.productId,
          variationId: item.variationId,
          quantity: parseInt(item.quantity, 10),
          unitPrice: parseFloat(item.unitPrice),
          notes: item.notes || null
        })),
        extraCharges: parseFloat(formData.extraCharges) || 0,
        extraChargesDescription: formData.extraChargesDescription || null,
        discountAmount: parseFloat(formData.discountAmount) || 0,
        discountDescription: formData.discountDescription || null,
        notes: formData.notes || null,
        status,
        autoReceive: formData.status === 'draft' && formData.autoReceive,
        paymentRecords: getPaymentRecordsPayload()
      };

      const response = await updatePurchaseOrder(orderId, updateData);
      
      success(t('purchaseOrder.purchaseOrderUpdated', { 
        orderNumber: response.purchaseOrder.purchaseOrderNumber 
      }), {
        title: t('common.success'),
        duration: 4000
      });
      
      navigate('/inventory/purchase-orders');
    } catch (err) {
      console.error('Purchase order update error:', err);
      const errorMessage = err.response?.data?.message || t('purchaseOrder.failedToUpdate');
      error(errorMessage, {
        title: t('common.error'),
        duration: 6000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(orderData);
    
    if (hasChanges) {
      const leave = await confirm(t('purchaseOrder.unsavedChanges'), {
        type: 'warning',
        title: t('common.unsavedChangesTitle'),
        confirmText: t('common.leave'),
        cancelText: t('common.stay'),
      });
      if (leave) {
        navigate('/inventory/purchase-orders');
      }
    } else {
      navigate('/inventory/purchase-orders');
    }
  };

  if (isLoadingOrder || isLoadingForm) {
    return (
      <div className={styles.createPurchaseOrderPage}>
        <PageHeader
          title={t('purchaseOrder.editPurchaseOrder')}
          description={t('purchaseOrder.editPurchaseOrderDescription')}
          icon={Package}
        />

        <div className={styles.pageContent}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinnerLarge}></div>
            <h3>{t('purchaseOrder.loadingOrder')}</h3>
            <p>{t('purchaseOrder.loadingOrderMessage')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.createPurchaseOrderPage}>
      <PageHeader
        title={t('purchaseOrder.editPurchaseOrder')}
        description={t('purchaseOrder.editPurchaseOrderDescription')}
        icon={Package}
        actions={
          <button 
            onClick={handleCancel} 
            className={styles.backButton}
            disabled={isSubmitting}
          >
            <ArrowLeft size={20} />
            {t('purchaseOrder.backToPurchaseOrders')}
          </button>
        }
      />

      {!isEditable && (
        <div className={styles.statusBanner}>
          <Lock size={16} />
          <span>{lockedByReceipts ? t('purchaseOrder.cannotEditActiveReceipts') : t('purchaseOrder.orderNotEditable', { status: t(`purchaseOrder.status${formData.status.charAt(0).toUpperCase() + formData.status.slice(1)}`) })}</span>
        </div>
      )}

      <div className={styles.pageContent}>
        <form className={styles.purchaseOrderForm}>
          <div className={styles.formSections}>
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <FileText className={styles.sectionIcon} size={20} />
                <h2>{t('purchaseOrder.basicInformation')}</h2>
                {!isEditable && (
                  <span className={styles.lockedLabel}>
                    <Lock size={16} />
                    {t('common.readOnly')}
                  </span>
                )}
              </div>
              <BasicInformationSection
                formData={formData}
                warehouses={warehouses}
                suppliers={suppliers}
                errors={errors}
                onFieldChange={handleFieldChange}
                onAddSupplier={addSupplier}
                disabled={!isEditable}
                isEditMode
              />
            </div>

            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <ShoppingCart className={styles.sectionIcon} size={20} />
                <h2>{t('purchaseOrder.purchaseOrderItems')}</h2>
                {!isEditable && (
                  <span className={styles.lockedLabel}>
                    <Lock size={16} />
                    {t('common.readOnly')}
                  </span>
                )}
              </div>
              <PurchaseOrderItemsSection
                items={formData.items}
                errors={errors}
                onAddItem={addItem}
                onUpdateItem={updateItem}
                onRemoveItem={removeItem}
                disabled={!isEditable}
              />
            </div>
          </div>

          <div className={styles.financialSidebar}>
            <div className={styles.sectionHeader}>
              <DollarSign className={styles.sectionIcon} size={20} />
              <h2>{t('purchaseOrder.financialDetails')}</h2>
            </div>
            <FinancialDetailsSection
              formData={formData}
              totals={totals}
              errors={errors}
              onFieldChange={handleFieldChange}
              disabled={!isEditable}
              beforeGrandTotal={isEditable && (
                <div className={styles.sidebarPaymentRecords}>
                  <div className={styles.sidebarSubHeader}>
                    <CreditCard className={styles.sectionIcon} size={18} />
                    <h3>{t('purchaseOrder.paymentRecords')}</h3>
                  </div>
                  <PaymentRecordsSection
                    paymentRecords={formData.paymentRecords}
                    paymentMethods={paymentMethods}
                    orderTotal={totals.grandTotal}
                    errors={errors}
                    required={false}
                    totalLabel={t('purchaseOrder.poTotal')}
                    onAddRecord={addPaymentRecord}
                    onUpdateRecord={updatePaymentRecord}
                    onRemoveRecord={removePaymentRecord}
                  />
                </div>
              )}
            />

            <div className={styles.formActions}>
              <button 
                type="button" 
                onClick={handleCancel}
                className={styles.cancelButton}
                disabled={isSubmitting}
              >
                <X size={18} />
                {t('common.cancel')}
              </button>
              {isEditable && (
                <>
                  {!formData.autoReceive && (
                    <button
                      type="button"
                      onClick={() => handleSubmit('draft')}
                      className={styles.draftButton}
                      disabled={isSubmitting}
                    >
                      <FileText size={18} />
                      {t('purchaseOrder.saveAsDraft')}
                    </button>
                  )}
                  <button 
                    type="button" 
                    onClick={() => handleSubmit('pending')}
                    className={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <div className={styles.loadingSpinner}></div>
                    ) : (
                      <>
                        <Save size={18} />
                        {t('purchaseOrder.updateOrder')}
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </form>
      </div>

      {showConfirmDialog && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <AlertCircle className={styles.dialogIcon} size={24} />
              <h3>{t('purchaseOrder.confirmUpdateTitle')}</h3>
            </div>
            <div className={styles.dialogBody}>
              <p>{t('purchaseOrder.confirmUpdate')}</p>
              <div className={styles.warningMessage}>
                <AlertCircle size={16} />
                <span>{t('purchaseOrder.updateWarning')}</span>
              </div>
            </div>
            <div className={styles.dialogActions}>
              <button
                onClick={() => setShowConfirmDialog(false)}
                className={styles.dialogCancelButton}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleSubmit(pendingStatus)}
                className={styles.dialogConfirmButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('purchaseOrder.updatingOrder') : t('common.confirmUpdate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditPurchaseOrderPage;
